import React from 'react';
import { StudentQAModel } from '../../../../models';
import { ArrowLeft, Calendar, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

interface StudentQADetailViewProps {
    qa: StudentQAModel;
    onBack: () => void;
}

const StudentQADetailView: React.FC<StudentQADetailViewProps> = ({ qa, onBack }) => {
    const formatDate = (ts: number) => {
        const date = new Date(ts);
        return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <button
                    onClick={onBack}
                    className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all flex items-center justify-center"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <Bot size={14} />
                        </div>
                        <h2 className="text-xl font-black text-slate-800">질문 상세</h2>
                    </div>
                    <p className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                        <Calendar size={12} />
                        {formatDate(qa.createdAt)}
                    </p>
                </div>
            </div>

            {/* Question */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold">질문</span>
                </div>
                <h3 className="text-lg font-black text-slate-800 leading-relaxed">
                    {qa.question}
                </h3>
                {qa.imageUrl && (
                    <div className="max-w-md rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                        <img src={qa.imageUrl} alt="Question" className="w-full h-auto" />
                    </div>
                )}
            </div>

            {/* AI Answer */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-100">
                        <Bot size={16} />
                    </div>
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold">AI 답변</span>
                </div>
                <div className="prose prose-slate max-w-none prose-sm font-medium leading-relaxed prose-headings:font-black prose-headings:text-slate-800 prose-p:text-slate-600 prose-strong:text-indigo-600">
                    <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeRaw, rehypeKatex]}
                    >
                        {qa.aiAnswer}
                    </ReactMarkdown>
                </div>
            </div>
        </div>
    );
};

export default StudentQADetailView;
